import { WorkflowStepType } from "@/constants/workflows/workflowCreate";
import { TwoAppFieldMapping, TwoAppMapping, WorkflowCreateRequestBodyV2 } from "@/types/WorkflowCreate/workflowCreate";
import { WorkflowInDetailV2 } from "@/types/WorkflowCreate/workflowDetailsV2";
import { normalizeWorkflowRequestBody } from "./normalizeWorkflowRequest";


export const buildDuplicateWorkflowRequestBody = (workflow: WorkflowInDetailV2) => {
  const nickname = `${workflow.nickname} - COPY`;

  const sortedTasks = [...workflow.workflowTasks].sort((a, b) => a.order - b.order);
  const taskById = new Map(sortedTasks.map(task => [task.id, task]));
  const iterateThroughByPair = new Map(
    workflow.workflowFieldMetaData.map(meta => [`${meta.sourceTaskId}:${meta.destinationTaskId}`, meta.iterateThrough])
  );

  // Include schedule if exists
  const workflowSchedule = workflow.workflowSchedule
    ? {
      scheduleType: workflow.workflowSchedule.scheduleType || "",
      timezone: workflow.workflowSchedule.timezone || "",
      cronExpression: workflow.workflowSchedule.cronExpression || "",
    }
    : undefined;

  // Build workflowTasks
  const workflowTasks = sortedTasks.map(task => ({
    appId: task.stepType === WorkflowStepType.TRANSFORMATION ? null : task.appId,
    stepType: task.stepType,
    action: task.action,
    subject: task.subject,
    credentialIds: task.stepType === WorkflowStepType.TRANSFORMATION
      ? []
      : task.workflowTaskCredentials?.map(cred => cred.credentialId) || [],
    order: task.order,
    filters: task.workflowTaskFilters?.map(filter => ({
      filter: filter.filter,
      filterValue: filter.filterValue
    })) || [],
    options: task.workflowTaskOptions?.map(option => ({
      option: option.option,
      optionValue: option.optionValue
    })) || []
  }));

  // Build fieldMappings
  const fieldMappings: TwoAppFieldMapping[] = [];
  if (workflow.workflowFields && workflow.workflowFields.length > 0) {
    const grouped = new Map<string, TwoAppMapping[]>();
    const fieldGroupMeta = new Map<string, {
      sourceAppId: string | null;
      destinationAppId: string;
      sourceTaskOrder: number;
      destinationTaskOrder: number;
      iterateThrough?: string;
    }>();

    workflow.workflowFields.forEach(field => {
      const sourceTask = taskById.get(field.sourceTaskId);
      const destinationTask = taskById.get(field.destinationTaskId);
      if (!sourceTask || !destinationTask) return;

      const iterateThrough = iterateThroughByPair.get(`${field.sourceTaskId}:${field.destinationTaskId}`) || undefined;
      const key = `${sourceTask.order}-${destinationTask.order}-${iterateThrough ?? "none"}`;

      if (!grouped.has(key)) {
        grouped.set(key, []);
        fieldGroupMeta.set(key, {
          sourceAppId: sourceTask.stepType === WorkflowStepType.TRANSFORMATION ? null : sourceTask.appId,
          destinationAppId: destinationTask.appId,
          sourceTaskOrder: sourceTask.order,
          destinationTaskOrder: destinationTask.order,
          iterateThrough
        });
      }

      const mappingObj: TwoAppMapping = {
        sourceField: field.sourceField,
        destinationField: field.destinationField,
        order: field.order,
      };

      if (field.options) {
        mappingObj.options = field.options;
      }

      grouped.get(key)?.push(mappingObj);
    });

    grouped.forEach((mappings, key) => {
      const meta = fieldGroupMeta.get(key);
      if (!meta) return;
      fieldMappings.push({
        sourceAppId: meta.sourceAppId,
        destinationAppId: meta.destinationAppId,
        sourceTaskOrder: meta.sourceTaskOrder,
        destinationTaskOrder: meta.destinationTaskOrder,
        iterateThrough: meta.iterateThrough,
        mappings
      });
    });
  }

  const transformationSteps = sortedTasks
    .filter(task => task.stepType === WorkflowStepType.TRANSFORMATION)
    .flatMap(task => task.transformationSteps?.map((step, index) => ({
      configuration: step.configuration,
      taskOrder: task.order,
      stepName: step.stepName,
      stepType: step.stepType,
      order: index + 1,
      isActive: step.isEnabled,
      isEnabled: step.isEnabled,
      validationRules: step.validationRules
    })) || []);

  // Build final payload
  const payload: WorkflowCreateRequestBodyV2 = {
    nickname,
    workflowTasks,
    fieldMappings,
    transformationSteps,
  };

  if (workflow.startingFrom) payload.startingFrom = workflow.startingFrom;
  if (workflowSchedule) payload.workflowSchedule = workflowSchedule;

  return normalizeWorkflowRequestBody(payload);
};
